import React, { useEffect, useRef } from "react";
import { NavLink, Link } from "react-router-dom";
import { X, ArrowUpRight, LayoutGrid, Star } from "lucide-react";
import { useLangDruk } from "@/components/menu/MenuSnelActie";
import { HubBadge } from "./HubBar";

function ToolRij({ it, count, favoriet, onFavoriet, onNavigate, onSnelActie }) {
  const Icon = it.icon;
  const druk = useLangDruk(() => onSnelActie?.(it));
  return (
    <div className="group relative">
      <NavLink to={it.to} onClick={onNavigate} data-testid={`flyout-item-${it.tid}`} {...(onSnelActie ? druk : {})}
        className={({ isActive }) => `flex items-start gap-3 rounded-lg pl-2.5 pr-9 py-2 border-l-2 transition-colors duration-150 select-none ${
          isActive ? "bg-blue-50 border-blue-600 text-blue-800" : "border-transparent text-slate-700 hover:bg-slate-100 hover:text-slate-900"}`}>
        <span className="mt-0.5 h-7 w-7 rounded-md bg-slate-100 group-hover:bg-white flex items-center justify-center shrink-0 text-slate-600 transition-colors">{Icon ? <Icon className="h-4 w-4" /> : null}</span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-[13px] font-semibold leading-tight truncate">{it.label}</span>
            <HubBadge n={count} />
          </span>
          {it.beschrijving && <span className="block text-[11px] text-slate-500 leading-snug truncate">{it.beschrijving}</span>}
        </span>
      </NavLink>
      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFavoriet(it); }} aria-label={favoriet ? "Bladwijzer verwijderen" : "Bladwijzer toevoegen"} data-testid={`flyout-fav-${it.tid}`}
        className={`absolute right-2 top-2 p-1 rounded-md transition-opacity ${favoriet ? "text-amber-500" : "text-slate-300 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-amber-500"}`}>
        <Star className={`h-3.5 w-3.5 ${favoriet ? "fill-amber-400" : ""}`} />
      </button>
    </div>
  );
}

/** Uitklappaneel van één hub: kop met uitleg en startpagina, tools per groep in 1–2 kolommen, voettekst naar 'Alle tools'. */
export function HubFlyout({ hub, counts, favorieten, onFavoriet, onClose, left, barRef, onSnelActie }) {
  const ref = useRef(null);
  useEffect(() => {
    const toets = (e) => { if (e.key === "Escape") { onClose(); barRef?.current?.querySelector(`[data-testid="hub-btn-${hub.key}"]`)?.focus(); } };
    const klik = (e) => { if (ref.current && !ref.current.contains(e.target) && !(barRef?.current && barRef.current.contains(e.target))) onClose(); };
    document.addEventListener("keydown", toets);
    document.addEventListener("mousedown", klik);
    return () => { document.removeEventListener("keydown", toets); document.removeEventListener("mousedown", klik); };
  }, [hub.key, onClose, barRef]);
  useEffect(() => { ref.current?.querySelector("a")?.focus({ preventScroll: true }); }, [hub.key]);
  const groepen = Object.entries(hub.items.reduce((acc, it) => ((acc[it.subgroup] = acc[it.subgroup] || []).push(it), acc), {}));
  const breed = hub.items.length > 8;
  const Icon = hub.icon;
  const favSet = new Set(favorieten.map((f) => f.to));
  return (
    <div ref={ref} role="dialog" aria-label={`${hub.label} — tools`} data-testid="hub-flyout-panel"
      className={`hidden lg:flex fixed top-0 bottom-0 z-40 flex-col bg-white border-r border-slate-200 shadow-[0_20px_45px_-10px_rgba(15,23,42,0.22),0_8px_16px_-6px_rgba(15,23,42,0.12)] animate-in fade-in slide-in-from-left-2 duration-200 ${breed ? "w-[720px]" : "w-[440px]"}`}
      style={{ left }}>
      <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50">
        <span className={`h-10 w-10 rounded-xl ${hub.kleur} text-white flex items-center justify-center shrink-0 shadow-sm`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold tracking-tight text-slate-900">{hub.label}</h2>
            <span className="text-[11px] text-slate-400">{hub.items.length} tools</span>
          </div>
          <p className="text-xs text-slate-500 leading-snug">{hub.uitleg}</p>
          <Link to={hub.hubTo} onClick={onClose} className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900" data-testid="hub-flyout-start-link">
            Naar startpagina {hub.label} <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <button type="button" onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-200 transition-colors" aria-label="Sluiten" data-testid="hub-flyout-close-btn"><X className="h-4 w-4" /></button>
      </div>
      <div className={`flex-1 overflow-y-auto px-4 py-4 ${breed ? "columns-2 gap-4 [column-fill:_balance]" : ""}`}>
        {groepen.map(([sub, items]) => (
          <section key={sub} className="break-inside-avoid mb-4" data-testid={`flyout-groep-${sub.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
            <div className="px-2.5 pb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{sub}</div>
            <div className="space-y-0.5">
              {items.map((it) => <ToolRij key={it.to} it={it} count={it.counter ? counts[it.counter] || 0 : 0} favoriet={favSet.has(it.to)} onFavoriet={onFavoriet} onNavigate={onClose} onSnelActie={onSnelActie} />)}
            </div>
          </section>
        ))}
      </div>
      <div className="border-t border-slate-200 px-5 py-3 bg-white">
        <Link to="/alle-tools" onClick={onClose} className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-slate-900" data-testid="hub-flyout-alle-tools">
          <LayoutGrid className="h-3.5 w-3.5" /> Alle tools bekijken
        </Link>
      </div>
    </div>
  );
}
